import React, { useEffect } from 'react';
import { Navigate, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { company } from '../../mock';
import { LayoutDashboard, Briefcase, Users, KanbanSquare, Bell, LogOut, CalendarClock, FileText, IdCard, CalendarCheck, UserCircle, BarChart3, Fingerprint, Wallet, Settings, Trophy, CalendarDays, Sparkles, BookOpen } from 'lucide-react';
import { hrmsGoogleAuth } from '../../lib/hrmsApi';
import { useToast } from '../../hooks/use-toast';

const ADMIN_NAV = [
  { to: '/hrms', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/hrms/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/hrms/jobs', label: 'Jobs', icon: Briefcase },
  { to: '/hrms/pipeline', label: 'Pipeline', icon: KanbanSquare },
  { to: '/hrms/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/hrms/interviews', label: 'Interviews', icon: CalendarClock },
  { to: '/hrms/offers', label: 'Offers', icon: FileText },
  { to: '/hrms/candidates', label: 'Candidates', icon: Users },
  { to: '/hrms/referrals', label: 'Referrals', icon: Trophy },
  { to: '/hrms/employees', label: 'Employees', icon: IdCard },
  { to: '/hrms/onboarding', label: 'Onboarding', icon: Sparkles },
  { to: '/hrms/handbook', label: 'Handbook', icon: BookOpen },
  { to: '/hrms/attendance', label: 'Attendance', icon: Fingerprint },
  { to: '/hrms/payroll', label: 'Payroll', icon: Wallet },
  { to: '/hrms/leaves', label: 'Leaves', icon: CalendarCheck },
  { to: '/hrms/notifications', label: 'Notifications', icon: Bell },
  { to: '/hrms/settings', label: 'Settings', icon: Settings },
  { to: '/hrms/me', label: 'My workspace', icon: UserCircle },
];

const EMPLOYEE_NAV = [
  { to: '/hrms/me', label: 'My workspace', icon: UserCircle, end: true },
  { to: '/hrms/me/onboarding', label: 'Onboarding', icon: Sparkles },
  { to: '/hrms/me/handbook', label: 'Handbook', icon: BookOpen },
  { to: '/hrms/notifications', label: 'Notifications', icon: Bell },
];

const HrmsLayout = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { toast } = useToast();
  const token = localStorage.getItem('hrms_token');
  const user = (() => { try { return JSON.parse(localStorage.getItem('hrms_user') || 'null'); } catch { return null; } })();

  // Handle Google OAuth callback (#session_id=...) landing on /hrms
  useEffect(() => {
    const hash = loc.hash || '';
    if (hash.includes('session_id=')) {
      const sid = new URLSearchParams(hash.substring(1)).get('session_id');
      if (sid) {
        (async () => {
          try {
            const res = await hrmsGoogleAuth(sid);
            localStorage.setItem('hrms_token', res.token);
            localStorage.setItem('hrms_user', JSON.stringify(res.user));
            toast({ title: `Welcome, ${res.user.name || res.user.email}` });
            // clear hash and reload
            window.history.replaceState(null, '', '/hrms');
            window.location.reload();
          } catch (err) {
            toast({ title: 'Google sign-in failed', description: err?.response?.data?.detail || '' });
            window.history.replaceState(null, '', '/hrms/login');
            nav('/hrms/login');
          }
        })();
      }
    }
  }, [loc.hash]);

  // If we're processing a session_id, show a loading state (don't redirect to login)
  const hasSessionId = (loc.hash || '').includes('session_id=');
  if (!token && hasSessionId) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Completing Google sign-in…</div>;
  }
  if (!token) return <Navigate to="/hrms/login" replace />;

  const logout = () => {
    localStorage.removeItem('hrms_token');
    localStorage.removeItem('hrms_user');
    nav('/hrms/login');
  };

  const isActive = (to, end) => end ? loc.pathname === to : loc.pathname === to || loc.pathname.startsWith(to + '/');
  const NAV = user?.role === 'employee' ? EMPLOYEE_NAV : ADMIN_NAV;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-slate-950 text-white flex flex-col shrink-0">
        <div className="px-5 h-16 flex items-center gap-2 border-b border-white/10">
          <img src={company.logo} alt="Hipro" className="h-8 w-8 rounded" />
          <div>
            <div className="font-bold text-sm">Hipro HRMS</div>
            <div className="text-[10px] text-slate-400">Internal portal</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive(n.to, n.end) ? 'bg-orange-500 text-white' : 'text-slate-200 hover:bg-white/10'}`} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-2">
          <div className="px-3 text-xs flex items-center gap-2">
            {user?.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> : <div className="h-7 w-7 rounded-full bg-orange-500 flex items-center justify-center font-bold">{(user?.name || user?.email || '?').charAt(0).toUpperCase()}</div>}
            <div className="min-w-0">
              <div className="font-semibold text-white truncate">{user?.name || user?.email}</div>
              <div className="text-slate-400 capitalize">{user?.role?.replace('_',' ')}</div>
            </div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/10" data-testid="hrms-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="text-slate-800 font-semibold">Welcome back, {user?.name || 'HR'}</div>
          <Link to="/" className="text-xs text-slate-500 hover:text-orange-600">Back to public site →</Link>
        </header>
        <div className="p-6"><Outlet /></div>
      </main>
    </div>
  );
};

export default HrmsLayout;
